/**
 * Login Page
 *
 * Per-tenant password authentication. Supports both demo password flow
 * and self-serve email+password accounts. Includes inline forgot-password flow.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../lib/auth-context";
import {
  Logo,
  Button,
  Input,
  Form,
  FormField,
  FormItem,
  FormControl,
  Alert,
  AlertTitle,
  AlertDescription,
  Spinner,
} from "@pentatonic-ltd/ui";
import { CheckCircle } from "@solar-icons/react";
import { InteractiveParticles } from "./InteractiveParticles";

interface LoginFormValues {
  email: string;
  password: string;
}

interface ForgotFormValues {
  email: string;
}

const ASSETS = {
  kiehls: "/images/kiehls.png",
  bq: "/images/bq.png",
  hyundai: "/images/hyundai.png",
  starbucks: "/images/starbucks.png",
};

function RightPanel() {
  return (
    <div className="hidden lg:flex flex-1 bg-[#171717] relative overflow-hidden">
      {/* Interactive particle field fills the panel */}
      <InteractiveParticles className="absolute inset-0" />

      {/* Brand logos overlay */}
      <div className="absolute bottom-12 left-0 right-0 flex flex-col gap-6 items-center pointer-events-none z-10">
        <p className="font-mono font-extralight text-[14px] uppercase tracking-[0.28px] text-white/60 text-center">
          Trusted by the world's leading consumer brands
        </p>
        <div className="flex gap-20 items-center">
          <div className="h-[37px] w-[74px] shrink-0">
            <img src={ASSETS.kiehls} alt="Kiehl's" className="h-full w-full object-contain object-left" />
          </div>
          <div className="h-[35px] w-[35px] shrink-0">
            <img src={ASSETS.bq} alt="B&Q" className="h-full w-full object-contain" />
          </div>
          <div className="h-[16px] w-[125px] shrink-0">
            <img src={ASSETS.hyundai} alt="Hyundai" className="h-full w-full object-contain" />
          </div>
          <div className="h-[37px] w-[37px] shrink-0">
            <img src={ASSETS.starbucks} alt="Starbucks" className="h-full w-full object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}

type Step = "login" | "forgot" | "forgot_sent" | "request_sent";

export function LoginPage() {
  const { login, isLoading: authLoading } = useAuth();
  const [step, setStep] = useState<Step>("login");
  const [authError, setAuthError] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const loginForm = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "" },
  });

  const forgotForm = useForm<ForgotFormValues>({
    defaultValues: { email: "" },
  });

  const onLogin = async (values: LoginFormValues) => {
    setAuthError(null);
    const result = await login(values.password, values.email || undefined);
    if (!result.success) {
      if (result.error === "email_not_verified") {
        setAuthError("Please verify your email before signing in. Check your inbox for the verification link.");
      } else if (result.error === "pending_approval") {
        setAuthError("Your account is awaiting approval from your brand's account owner.");
      } else if (result.error === "account_denied") {
        setAuthError("account_denied");
      } else {
        setAuthError(result.error || "Authentication failed");
      }
    }
  };

  const onReRequest = async () => {
    const email = loginForm.getValues("email");
    const password = loginForm.getValues("password");
    try {
      const res = await fetch("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json() as { success: boolean };
      if (result.success) {
        setAuthError(null);
        setStep("request_sent");
      }
    } catch {
      // Silently fail — they can try again
    }
  };

  const onForgot = async (values: ForgotFormValues) => {
    setForgotError(null);
    try {
      const res = await fetch("/api/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email }),
      });
      const result = await res.json() as { success: boolean; data?: { resetToken?: string } };
      if (!result.success) {
        setForgotError("Something went wrong. Please try again.");
        return;
      }
      setResetToken(result.data?.resetToken ?? null);
      setStep("forgot_sent");
    } catch {
      setForgotError("Something went wrong. Please try again.");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#171717] flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex bg-[#171717]">
      <div className="w-full lg:w-[560px] lg:shrink-0 bg-white dark:bg-[#171717] flex flex-col min-h-screen">
        <div className="flex flex-col flex-1 w-full max-w-[560px] mx-auto">
          {/* Logo — pinned top */}
          <div className="px-10 pt-12 shrink-0">
            <a href="/"><Logo variant="auto" size="lg" /></a>
          </div>

          {/* ── Sign in ──────────────────────────────────────────────── */}
          {step === "login" && (
            <>
              <div className="flex-1 flex flex-col px-10 pb-10">
                <div className="flex flex-col gap-8 pt-20">
                  <div className="flex flex-col gap-2">
                    <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                      Welcome back
                    </h1>
                    <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                      Sign in to your account
                    </p>
                  </div>

                  <Form form={loginForm} onSubmit={onLogin} className="flex flex-col gap-6">
                    {authError === "account_denied" ? (
                      <Alert variant="warning">
                        <AlertTitle>Access not granted</AlertTitle>
                        <AlertDescription>
                          You don't currently have access to this account.{" "}
                          <button
                            type="button"
                            onClick={onReRequest}
                            className="underline underline-offset-2 font-medium"
                          >
                            Request access again →
                          </button>
                        </AlertDescription>
                      </Alert>
                    ) : authError ? (
                      <Alert variant="error">
                        <AlertTitle>Couldn't sign in</AlertTitle>
                        <AlertDescription>{authError}</AlertDescription>
                      </Alert>
                    ) : null}

                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="email"
                              label="Email address"
                              variant={fieldState.error ? "error" : "default"}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={loginForm.control}
                      name="password"
                      rules={{ required: "Password is required" }}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              passwordToggle
                              label="Password"
                              variant={fieldState.error ? "error" : "default"}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <button
                      type="button"
                      onClick={() => { setForgotError(null); setStep("forgot"); }}
                      className="text-[14px] text-[#A9A9A9] hover:text-[#171717] dark:hover:text-white text-left transition-colors"
                    >
                      Forgot password?
                    </button>

                    <Button
                      type="submit"
                      variant="primary"
                      fullWidth
                      loading={loginForm.formState.isSubmitting}
                    >
                      Sign in
                    </Button>
                  </Form>
                </div>
              </div>

              <div className="px-10 pb-10 shrink-0">
                <p className="text-[14px] text-[#A9A9A9]">
                  New to Pentatonic?{" "}
                  <a href="/signup" className="text-[#171717] dark:text-white underline underline-offset-2">
                    Create account →
                  </a>
                </p>
              </div>
            </>
          )}

          {/* ── Forgot password ──────────────────────────────────────── */}
          {step === "forgot" && (
            <>
              <div className="flex-1 flex flex-col px-10 pb-10">
                <div className="flex flex-col gap-8 pt-20">
                  <div className="flex flex-col gap-2">
                    <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                      Reset your password
                    </h1>
                    <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                      Enter your work email and we'll send you a reset link.
                    </p>
                  </div>

                  <Form form={forgotForm} onSubmit={onForgot} className="flex flex-col gap-6">
                    {forgotError && (
                      <Alert variant="error">
                        <AlertTitle>Couldn't send link</AlertTitle>
                        <AlertDescription>{forgotError}</AlertDescription>
                      </Alert>
                    )}

                    <FormField
                      control={forgotForm.control}
                      name="email"
                      rules={{ required: "Email is required" }}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="email"
                              label="Email address"
                              variant={fieldState.error ? "error" : "default"}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      fullWidth
                      loading={forgotForm.formState.isSubmitting}
                    >
                      Send reset link
                    </Button>
                  </Form>
                </div>
              </div>

              <div className="px-10 pb-10 shrink-0">
                <button
                  onClick={() => setStep("login")}
                  className="text-[14px] text-[#171717] dark:text-white underline underline-offset-2 cursor-pointer"
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

          {/* ── Request sent ─────────────────────────────────────────── */}
          {step === "request_sent" && (
            <>
              <div className="flex-1 flex flex-col px-10 pb-10">
                <div className="flex flex-col gap-6 pt-20">
                  <CheckCircle size={48} color="#171717" weight="LineDuotone" />
                  <div className="flex flex-col gap-2">
                    <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                      Request sent
                    </h1>
                    <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                      Your request has been resubmitted. You'll be able to sign in once the account owner approves it.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-10 pb-10 shrink-0">
                <button
                  onClick={() => setStep("login")}
                  className="text-[14px] text-[#171717] dark:text-white underline underline-offset-2 cursor-pointer"
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

          {/* ── Forgot sent ──────────────────────────────────────────── */}
          {step === "forgot_sent" && (
            <>
              <div className="flex-1 flex flex-col px-10 pb-10">
                <div className="flex flex-col gap-6 pt-20">
                  <CheckCircle size={48} color="#171717" weight="LineDuotone" />
                  <div className="flex flex-col gap-2">
                    <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                      Check your email
                    </h1>
                    <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                      If an account exists for{" "}
                      <span className="text-[#171717] dark:text-white">{forgotForm.getValues("email")}</span>
                      , you'll receive a reset link shortly.
                    </p>
                  </div>

                  {resetToken && (
                    <div className="p-3 border border-[#A9A9A9] bg-[#FAFAFA] font-mono font-extralight text-[14px] uppercase tracking-wider text-[#171717]">
                      Dev stub — reset link:{" "}
                      <a href={`/reset-password?token=${resetToken}`} className="underline">
                        /reset-password?token={resetToken}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-10 pb-10 shrink-0">
                <button
                  onClick={() => setStep("login")}
                  className="text-[14px] text-[#171717] dark:text-white underline underline-offset-2 cursor-pointer"
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

        </div>
      </div>
      <RightPanel />
    </div>
  );
}
